import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, UsersIcon, BuildingIcon, CalendarIcon, DollarSignIcon, MapPinIcon, MonitorIcon, TruckIcon, UtensilsIcon, ShieldIcon, ClipboardCheckIcon, ArrowRightIcon, BarChart3Icon, GlobeIcon, MicIcon, WrenchIcon, FileTextIcon, PhoneIcon, SendIcon, SparklesIcon, AlertCircleIcon, ChevronRightIcon } from 'lucide-react';
export function ElevateImpact() {
  const calculatorRef = useRef<HTMLDivElement>(null);
  // Calculator state
  const [headcount, setHeadcount] = useState(150);
  const [nights, setNights] = useState('3');
  const [baselineCity, setBaselineCity] = useState('Vegas');
  const [meetingDays, setMeetingDays] = useState('2');
  const [experienceLevel, setExperienceLevel] = useState('Elevated');
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    employeeBand: '',
    headcountBand: '',
    targetMonth: '',
    formNights: '',
    considering: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Calculator logic
  const calculateEstimates = () => {
    const n = parseInt(nights);
    const md = parseInt(meetingDays);
    const hc = Math.max(headcount, 1);
    // Per-person-per-night base rates by city (USD)
    const cityRates: Record<string, {
      low: number;
      high: number;
    }> = {
      Vegas: {
        low: 380,
        high: 520
      },
      Scottsdale: {
        low: 400,
        high: 550
      },
      Austin: {
        low: 360,
        high: 490
      },
      Miami: {
        low: 420,
        high: 580
      },
      Other: {
        low: 370,
        high: 510
      }
    };
    // Experience level multipliers
    const expMultipliers: Record<string, number> = {
      Simple: 0.8,
      Elevated: 1.0,
      Premium: 1.35
    };
    // Meeting day add-on per person
    const meetingDayCost = md * 85;
    const rates = cityRates[baselineCity] || cityRates['Other'];
    const expMult = expMultipliers[experienceLevel] || 1.0;
    const usLow = Math.round((rates.low * n * hc + meetingDayCost * hc) * expMult);
    const usHigh = Math.round((rates.high * n * hc + meetingDayCost * hc) * expMult);
    // Cancun is typically 22-32% less
    const cancunLow = Math.round(usLow * 0.68);
    const cancunHigh = Math.round(usHigh * 0.78);
    // Calculate savings — compare apples to apples (low vs low, high vs high)
    const savingsLow = Math.max(0, usLow - cancunLow);
    const savingsHigh = Math.max(0, usHigh - cancunHigh);
    const savingsPctLow = usLow > 0 ? Math.round(savingsLow / usLow * 100) : 0;
    const savingsPctHigh = usHigh > 0 ? Math.round(savingsHigh / usHigh * 100) : 0;
    // Ensure the range reads correctly (lower number first)
    const pctMin = Math.min(savingsPctLow, savingsPctHigh);
    const pctMax = Math.max(savingsPctLow, savingsPctHigh);
    const avgSavings = Math.round((savingsLow + savingsHigh) / 2);
    const addOns: string[] = [];
    if (avgSavings >= 8000) addOns.push('Executive dinner with live entertainment');
    if (avgSavings >= 15000) addOns.push('Branded stage + AV upgrade');
    if (avgSavings >= 25000) addOns.push('Awards night production');
    if (avgSavings >= 35000) addOns.push('Team excursion day (cenotes, ruins, catamaran)');
    if (avgSavings >= 50000) addOns.push('Full video production + recap content');
    return {
      usRange: `$${(usLow / 1000).toFixed(0)}K – $${(usHigh / 1000).toFixed(0)}K`,
      cancunRange: `$${(cancunLow / 1000).toFixed(0)}K – $${(cancunHigh / 1000).toFixed(0)}K`,
      savingsRange: `$${(savingsLow / 1000).toFixed(0)}K – $${(savingsHigh / 1000).toFixed(0)}K`,
      savingsPct: pctMin === pctMax ? `${pctMax}%` : `${pctMin}–${pctMax}%`,
      addOns
    };
  };
  const estimates = calculateEstimates();
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('SKO Proposal Request:', formData);
    setFormSubmitted(true);
  };
  return <div className="pt-20 bg-white" data-id="element-1916">
      {/* ===== 1. HERO ===== */}
      <section className="relative py-20 md:py-28 overflow-hidden min-h-[560px]" data-id="element-1917">
        <div className="absolute inset-0 z-0 bg-[#374047]" data-id="element-1918"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1919">
          <div className="grid lg:grid-cols-2 gap-10 items-center" data-id="element-1920">
            <div data-id="element-1921">
              <div className="inline-flex items-center px-4 py-2 bg-[#0099A1]/20 backdrop-blur-sm rounded-full text-[#0099A1] text-sm font-semibold mb-6 border border-[#0099A1]/30" data-id="element-1922">
                <MapPinIcon className="h-4 w-4 mr-2" data-id="element-1923" />
                Cancun SKO Execution Partner
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight" data-id="element-1924">
                High-Impact Cancun SKOs
              </h1>
              <p className="text-xl md:text-2xl text-[#0099A1] font-semibold mb-6" data-id="element-1925">
                Built for Revenue Teams of 60–500+
              </p>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-xl" data-id="element-1926">
                We handle resort contracting, room blocks, meeting production,
                AV, transport, F&B, and executive experiences — executed
                end-to-end in Cancun.
              </p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-10 text-sm text-white/70" data-id="element-1927">
                <span className="flex items-center gap-1.5" data-id="element-1928">
                  <CheckIcon className="h-4 w-4 text-[#0099A1]" data-id="element-1929" />
                  10+ years in Cancun
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1930">
                  <CheckIcon className="h-4 w-4 text-[#0099A1]" data-id="element-1931" />
                  Thousands of events executed
                </span>
                <span className="flex items-center gap-1.5" data-id="element-1932">
                  <CheckIcon className="h-4 w-4 text-[#0099A1]" data-id="element-1933" />
                  Trusted local resort + production partners
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1934">
                <Button variant="primary" size="lg" to="/contact" data-id="element-1935">
                  Get a Cancun SKO Proposal
                </Button>
                <Button variant="secondary" size="lg" onClick={scrollToCalculator} className="bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20" data-id="element-1936">
                  Compare Cancun vs U.S. Cost
                </Button>
              </div>
            </div>
            <div className="hidden lg:block" data-id="element-1937">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-1938">
                <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop" alt="Corporate conference setup with professional staging" className="w-full h-[480px] object-cover" data-id="element-1939" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#374047]/40 via-transparent to-transparent" data-id="element-1940"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. WHY CANCUN VS U.S. HUBS ===== */}
      <section className="py-20 bg-[#F4E8D0]/25" data-id="element-1941">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1942">
          <div className="max-w-4xl mx-auto" data-id="element-1943">
            <div className="text-center mb-14" data-id="element-1944">
              <span className="inline-flex items-center px-4 py-1.5 bg-[#0099A1]/10 rounded-full text-[#0099A1] text-sm font-semibold mb-4" data-id="element-1945">
                <BarChart3Icon className="h-4 w-4 mr-2" data-id="element-1946" />
                The Business Case
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1947">
                Why Cancun Outperforms U.S. Hubs for SKOs
              </h2>
              <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-1948">
                It's not about the beach. It's about better execution economics,
                consolidated logistics, and higher production value per dollar.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1949">
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-1950">
                <div className="w-11 h-11 bg-[#0099A1]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1951">
                  <DollarSignIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1952" />
                </div>
                <h3 className="font-bold text-[#374047] mb-2 text-lg" data-id="element-1953">
                  20–30% Better Total Value
                </h3>
                <p className="text-[#374047]/65 text-sm leading-relaxed" data-id="element-1954">
                  All-inclusive resort pricing bundles rooms, F&B, meeting
                  space, and entertainment — often 20–30% below comparable U.S.
                  hub costs.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-1955">
                <div className="w-11 h-11 bg-[#0099A1]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1956">
                  <BuildingIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1957" />
                </div>
                <h3 className="font-bold text-[#374047] mb-2 text-lg" data-id="element-1958">
                  Better Large-Group Inventory
                </h3>
                <p className="text-[#374047]/65 text-sm leading-relaxed" data-id="element-1959">
                  Cancun resorts are built for groups of 100–600+. No scrambling
                  for room blocks across multiple hotels.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-1960">
                <div className="w-11 h-11 bg-[#0099A1]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1961">
                  <ShieldIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1962" />
                </div>
                <h3 className="font-bold text-[#374047] mb-2 text-lg" data-id="element-1963">
                  One-Roof Logistics
                </h3>
                <p className="text-[#374047]/65 text-sm leading-relaxed" data-id="element-1964">
                  Rooms, meals, meetings, and evening events under one roof.
                  Fewer vendors, fewer failure points, tighter execution.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-1965">
                <div className="w-11 h-11 bg-[#0099A1]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1966">
                  <UtensilsIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1967" />
                </div>
                <h3 className="font-bold text-[#374047] mb-2 text-lg" data-id="element-1968">
                  Hospitality Consistency
                </h3>
                <p className="text-[#374047]/65 text-sm leading-relaxed" data-id="element-1969">
                  All-inclusive means every meal, every drink, every snack is
                  covered. No per-diem headaches, no surprise F&B invoices.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-1970">
                <div className="w-11 h-11 bg-[#0099A1]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1971">
                  <MicIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1972" />
                </div>
                <h3 className="font-bold text-[#374047] mb-2 text-lg" data-id="element-1973">
                  More Production per Dollar
                </h3>
                <p className="text-[#374047]/65 text-sm leading-relaxed" data-id="element-1974">
                  AV, staging, lighting, and service labor costs are
                  significantly lower. Your production budget goes 40–60%
                  further.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-1975">
                <div className="w-11 h-11 bg-[#0099A1]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1976">
                  <GlobeIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1977" />
                </div>
                <h3 className="font-bold text-[#374047] mb-2 text-lg" data-id="element-1978">
                  Easy Access from U.S.
                </h3>
                <p className="text-[#374047]/65 text-sm leading-relaxed" data-id="element-1979">
                  Direct flights from 40+ U.S. cities. 2–4 hour flight times. No
                  passport issues for most teams (U.S. passport or passport
                  card).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3. COST & EFFICIENCY CALCULATOR ===== */}
      <section className="py-20 bg-white" ref={calculatorRef} data-id="element-1980">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1981">
          <div className="max-w-4xl mx-auto" data-id="element-1982">
            <div className="text-center mb-12" data-id="element-1983">
              <span className="inline-flex items-center px-4 py-1.5 bg-[#FF6B65]/10 rounded-full text-[#FF6B65] text-sm font-semibold mb-4" data-id="element-1984">
                <DollarSignIcon className="h-4 w-4 mr-2" data-id="element-1985" />
                Interactive Estimate
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1986">
                Cancun vs. U.S. Cost Comparison
              </h2>
              <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-1987">
                See how your SKO budget stacks up. Adjust the inputs to match
                your event.
              </p>
            </div>

            <div className="bg-[#F4E8D0]/20 rounded-2xl border border-[#F4E8D0] p-6 md:p-10" data-id="element-1988">
              {/* Inputs */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10" data-id="element-1989">
                <div data-id="element-1990">
                  <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-1991">
                    Headcount
                  </label>
                  <input type="number" value={headcount} onChange={e => setHeadcount(parseInt(e.target.value) || 0)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-base" min="10" max="1000" data-id="element-1992" />
                </div>
                <div data-id="element-1993">
                  <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-1994">
                    Nights
                  </label>
                  <select value={nights} onChange={e => setNights(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-base bg-white" data-id="element-1995">
                    <option value="2" data-id="element-1996">2 nights</option>
                    <option value="3" data-id="element-1997">3 nights</option>
                    <option value="4" data-id="element-1998">4 nights</option>
                  </select>
                </div>
                <div data-id="element-1999">
                  <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2000">
                    U.S. Baseline City
                  </label>
                  <select value={baselineCity} onChange={e => setBaselineCity(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-base bg-white" data-id="element-2001">
                    <option value="Vegas" data-id="element-2002">Las Vegas</option>
                    <option value="Scottsdale" data-id="element-2003">Scottsdale</option>
                    <option value="Austin" data-id="element-2004">Austin</option>
                    <option value="Miami" data-id="element-2005">Miami</option>
                    <option value="Other" data-id="element-2006">Other U.S. Hub</option>
                  </select>
                </div>
                <div data-id="element-2007">
                  <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2008">
                    Meeting Days
                  </label>
                  <select value={meetingDays} onChange={e => setMeetingDays(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-base bg-white" data-id="element-2009">
                    <option value="1" data-id="element-2010">1 day</option>
                    <option value="2" data-id="element-2011">2 days</option>
                    <option value="3" data-id="element-2012">3 days</option>
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-2" data-id="element-2013">
                  <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2014">
                    Experience Level
                  </label>
                  <div className="flex gap-3" data-id="element-2015">
                    {['Simple', 'Elevated', 'Premium'].map(level => <button key={level} onClick={() => setExperienceLevel(level)} className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium text-sm transition-colors ${experienceLevel === level ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/40'}`} data-id="element-2016">
                        {level}
                      </button>)}
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-[#374047] rounded-2xl p-6 md:p-8 text-white" data-id="element-2017">
                <div className="grid sm:grid-cols-3 gap-6 mb-6" data-id="element-2018">
                  <div data-id="element-2019">
                    <div className="text-xs uppercase tracking-wider text-white/50 mb-1" data-id="element-2020">
                      Est. {baselineCity} Cost
                    </div>
                    <div className="text-2xl md:text-3xl font-bold" data-id="element-2021">
                      {estimates.usRange}
                    </div>
                  </div>
                  <div data-id="element-2022">
                    <div className="text-xs uppercase tracking-wider text-[#0099A1] mb-1" data-id="element-2023">
                      Est. Cancun Cost
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-[#0099A1]" data-id="element-2024">
                      {estimates.cancunRange}
                    </div>
                  </div>
                  <div data-id="element-2025">
                    <div className="text-xs uppercase tracking-wider text-[#FF6B65] mb-1" data-id="element-2026">
                      Est. Savings
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-[#FF6B65]" data-id="element-2027">
                      {estimates.savingsPct}
                    </div>
                  </div>
                </div>

                {estimates.addOns.length > 0 && <div className="border-t border-white/10 pt-6" data-id="element-2028">
                    <p className="text-sm text-white/60 mb-3 font-medium" data-id="element-2029">
                      What you could add with the savings:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-2030">
                      {estimates.addOns.map((addon, i) => <span key={i} className="inline-flex items-center px-3 py-1.5 bg-white/10 rounded-full text-xs text-white/80" data-id="element-2031">
                          <SparklesIcon className="h-3 w-3 mr-1.5 text-[#FF6B65]" data-id="element-2032" />
                          {addon}
                        </span>)}
                    </div>
                  </div>}
              </div>

              <div className="flex items-start gap-2 mt-4 text-xs text-[#374047]/50" data-id="element-2033">
                <AlertCircleIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" data-id="element-2034" />
                <span data-id="element-2035">
                  Estimates based on typical market rates. Final pricing depends
                  on specific requirements, dates, and resort availability.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 4. PACKAGES BY HEADCOUNT ===== */}
      <section className="py-20 bg-[#F4E8D0]/25" data-id="element-2036">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2037">
          <div className="text-center mb-14" data-id="element-2038">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2039">
              SKO Packages by Team Size
            </h2>
            <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-2040">
              Scoped to your headcount. Each package is fully customizable.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-2041">
            {/* Tile 1: 60-120 */}
            <Card className="border border-gray-100 overflow-hidden" data-id="element-2042">
              <CardHeader className="bg-[#0099A1] text-white p-6" data-id="element-2043">
                <div className="text-sm font-medium text-white/80 mb-1" data-id="element-2044">
                  Team Size
                </div>
                <h3 className="text-2xl font-bold" data-id="element-2045">60–120 People</h3>
                <p className="text-sm text-white/80 mt-1" data-id="element-2046">2–3 nights</p>
              </CardHeader>
              <CardBody className="p-6" data-id="element-2047">
                <div className="space-y-4 mb-6" data-id="element-2048">
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-2" data-id="element-2049">
                    <span className="text-[#374047]/60" data-id="element-2050">
                      Typical room nights
                    </span>
                    <span className="font-semibold text-[#374047]" data-id="element-2051">
                      120–360
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-2" data-id="element-2052">
                    <span className="text-[#374047]/60" data-id="element-2053">Meeting footprint</span>
                    <span className="font-semibold text-[#374047]" data-id="element-2054">
                      2,000–4,000 sq ft
                    </span>
                  </div>
                </div>
                <h4 className="text-sm font-bold text-[#374047] mb-3" data-id="element-2055">
                  Typically includes:
                </h4>
                <ul className="space-y-2 mb-6" data-id="element-2056">
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2057">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2058" />
                    <span data-id="element-2059">Single resort buyout or dedicated wing</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2060">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2061" />
                    <span data-id="element-2062">1 main plenary + 2 breakout rooms</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2063">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2064" />
                    <span data-id="element-2065">AV + staging for general sessions</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2066">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2067" />
                    <span data-id="element-2068">Airport transfers + onsite coordination</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2069">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2070" />
                    <span data-id="element-2071">Welcome reception + 1 executive dinner</span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full" to="/contact" data-id="element-2072">
                  Get Proposal
                </Button>
              </CardBody>
            </Card>

            {/* Tile 2: 150-300 */}
            <Card className="border-2 border-[#0099A1] overflow-hidden shadow-lg relative" data-id="element-2073">
              <div className="absolute top-0 right-0 bg-[#FF6B65] text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-2074">
                Most Common
              </div>
              <CardHeader className="bg-[#0099A1] text-white p-6" data-id="element-2075">
                <div className="text-sm font-medium text-white/80 mb-1" data-id="element-2076">
                  Team Size
                </div>
                <h3 className="text-2xl font-bold" data-id="element-2077">150–300 People</h3>
                <p className="text-sm text-white/80 mt-1" data-id="element-2078">3 nights</p>
              </CardHeader>
              <CardBody className="p-6" data-id="element-2079">
                <div className="space-y-4 mb-6" data-id="element-2080">
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-2" data-id="element-2081">
                    <span className="text-[#374047]/60" data-id="element-2082">
                      Typical room nights
                    </span>
                    <span className="font-semibold text-[#374047]" data-id="element-2083">
                      450–900
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-2" data-id="element-2084">
                    <span className="text-[#374047]/60" data-id="element-2085">Meeting footprint</span>
                    <span className="font-semibold text-[#374047]" data-id="element-2086">
                      5,000–10,000 sq ft
                    </span>
                  </div>
                </div>
                <h4 className="text-sm font-bold text-[#374047] mb-3" data-id="element-2087">
                  Typically includes:
                </h4>
                <ul className="space-y-2 mb-6" data-id="element-2088">
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2089">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2090" />
                    <span data-id="element-2091">
                      Full resort group block with dedicated event space
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2092">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2093" />
                    <span data-id="element-2094">Ballroom plenary + 4–6 breakout rooms</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2095">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2096" />
                    <span data-id="element-2097">Full AV production with branded staging</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2098">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2099" />
                    <span data-id="element-2100">Managed transfers + onsite ops team</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2101">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2102" />
                    <span data-id="element-2103">Welcome reception, awards dinner, team activity</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2104">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2105" />
                    <span data-id="element-2106">Contingency planning + backup logistics</span>
                  </li>
                </ul>
                <Button variant="primary" className="w-full" to="/contact" data-id="element-2107">
                  Get Proposal
                </Button>
              </CardBody>
            </Card>

            {/* Tile 3: 300-600+ */}
            <Card className="border border-gray-100 overflow-hidden" data-id="element-2108">
              <CardHeader className="bg-[#0099A1] text-white p-6" data-id="element-2109">
                <div className="text-sm font-medium text-white/80 mb-1" data-id="element-2110">
                  Team Size
                </div>
                <h3 className="text-2xl font-bold" data-id="element-2111">300–600+ People</h3>
                <p className="text-sm text-white/80 mt-1" data-id="element-2112">3–4 nights</p>
              </CardHeader>
              <CardBody className="p-6" data-id="element-2113">
                <div className="space-y-4 mb-6" data-id="element-2114">
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-2" data-id="element-2115">
                    <span className="text-[#374047]/60" data-id="element-2116">
                      Typical room nights
                    </span>
                    <span className="font-semibold text-[#374047]" data-id="element-2117">
                      900–2,400+
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-b border-gray-100 pb-2" data-id="element-2118">
                    <span className="text-[#374047]/60" data-id="element-2119">Meeting footprint</span>
                    <span className="font-semibold text-[#374047]" data-id="element-2120">
                      10,000–25,000 sq ft
                    </span>
                  </div>
                </div>
                <h4 className="text-sm font-bold text-[#374047] mb-3" data-id="element-2121">
                  Typically includes:
                </h4>
                <ul className="space-y-2 mb-6" data-id="element-2122">
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2123">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2124" />
                    <span data-id="element-2125">
                      Multi-resort coordination or full property buyout
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2126">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2127" />
                    <span data-id="element-2128">Convention-center-scale plenary + 8+ breakouts</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2129">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2130" />
                    <span data-id="element-2131">
                      Enterprise AV: LED walls, multi-camera, livestream
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2132">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2133" />
                    <span data-id="element-2134">Fleet transfers + dedicated ops command center</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-[#374047]/75" data-id="element-2135">
                    <CheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2136" />
                    <span data-id="element-2137">
                      Multi-night programming: dinners, awards, excursions
                    </span>
                  </li>
                </ul>
                <Button variant="outline" className="w-full" to="/contact" data-id="element-2138">
                  Get Proposal
                </Button>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* ===== 5. WHAT'S INCLUDED ===== */}
      <section className="py-20 bg-white" data-id="element-2139">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2140">
          <div className="max-w-4xl mx-auto" data-id="element-2141">
            <div className="text-center mb-14" data-id="element-2142">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2143">
                End-to-End Execution
              </h2>
              <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-2144">
                You focus on the content and the people. We handle everything
                else.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2145">
              <div className="flex items-start gap-4 p-5 bg-[#F4E8D0]/20 rounded-xl" data-id="element-2146">
                <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2147">
                  <BuildingIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2148" />
                </div>
                <div data-id="element-2149">
                  <h3 className="font-bold text-[#374047] mb-1" data-id="element-2150">
                    Resort Negotiation + Room Blocks
                  </h3>
                  <p className="text-sm text-[#374047]/60" data-id="element-2151">
                    Leveraging 10+ years of resort relationships for best rates
                    and terms.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#F4E8D0]/20 rounded-xl" data-id="element-2152">
                <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2153">
                  <MonitorIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2154" />
                </div>
                <div data-id="element-2155">
                  <h3 className="font-bold text-[#374047] mb-1" data-id="element-2156">
                    Meeting Space + AV/Production
                  </h3>
                  <p className="text-sm text-[#374047]/60" data-id="element-2157">
                    Staging, lighting, sound, screens, and technical direction —
                    all sourced locally.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#F4E8D0]/20 rounded-xl" data-id="element-2158">
                <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2159">
                  <TruckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2160" />
                </div>
                <div data-id="element-2161">
                  <h3 className="font-bold text-[#374047] mb-1" data-id="element-2162">
                    Transfers + Onsite Ops
                  </h3>
                  <p className="text-sm text-[#374047]/60" data-id="element-2163">
                    Airport pickups, inter-venue transport, and a dedicated
                    onsite operations team.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#F4E8D0]/20 rounded-xl" data-id="element-2164">
                <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2165">
                  <UtensilsIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2166" />
                </div>
                <div data-id="element-2167">
                  <h3 className="font-bold text-[#374047] mb-1" data-id="element-2168">
                    F&B Planning
                  </h3>
                  <p className="text-sm text-[#374047]/60" data-id="element-2169">
                    Menu curation, dietary accommodations, private dining
                    coordination, and bar service.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#F4E8D0]/20 rounded-xl" data-id="element-2170">
                <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2171">
                  <SparklesIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2172" />
                </div>
                <div data-id="element-2173">
                  <h3 className="font-bold text-[#374047] mb-1" data-id="element-2174">
                    Executive Dinners + Experiences
                  </h3>
                  <p className="text-sm text-[#374047]/60" data-id="element-2175">
                    Awards nights, team activities, cultural excursions, and VIP
                    entertainment.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 bg-[#F4E8D0]/20 rounded-xl" data-id="element-2176">
                <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2177">
                  <WrenchIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2178" />
                </div>
                <div data-id="element-2179">
                  <h3 className="font-bold text-[#374047] mb-1" data-id="element-2180">
                    Contingency Planning
                  </h3>
                  <p className="text-sm text-[#374047]/60" data-id="element-2181">
                    Weather backup plans, vendor redundancy, and real-time
                    problem resolution.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 6. RESORT & PRODUCTION CAPABILITY ===== */}
      <section className="py-20 bg-[#374047]" data-id="element-2182">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2183">
          <div className="max-w-5xl mx-auto" data-id="element-2184">
            <div className="text-center mb-14" data-id="element-2185">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2186">
                Resort & Production Capability
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto" data-id="element-2187">
                We don't send you a list of 20 resorts. We send you 2–3
                shortlisted options with meeting specs, pricing, and our
                recommendation.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-2188">
              <div className="rounded-xl overflow-hidden" data-id="element-2189">
                <img src="https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=2012&auto=format&fit=crop" alt="Corporate meeting setup with staging" className="w-full h-72 object-cover" data-id="element-2190" />
              </div>
              <div className="rounded-xl overflow-hidden" data-id="element-2191">
                <img src="https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=2070&auto=format&fit=crop" alt="Professional event production and AV setup" className="w-full h-72 object-cover" data-id="element-2192" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2193">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center" data-id="element-2194">
                <div className="text-3xl font-bold text-[#0099A1] mb-1" data-id="element-2195">
                  15+
                </div>
                <div className="text-sm text-white/60" data-id="element-2196">
                  Vetted resort partners
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center" data-id="element-2197">
                <div className="text-3xl font-bold text-[#0099A1] mb-1" data-id="element-2198">
                  2–3
                </div>
                <div className="text-sm text-white/60" data-id="element-2199">
                  Shortlisted options per client
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center" data-id="element-2200">
                <div className="text-3xl font-bold text-[#0099A1] mb-1" data-id="element-2201">
                  Local
                </div>
                <div className="text-sm text-white/60" data-id="element-2202">
                  AV + production crews
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center" data-id="element-2203">
                <div className="text-3xl font-bold text-[#0099A1] mb-1" data-id="element-2204">
                  48hr
                </div>
                <div className="text-sm text-white/60" data-id="element-2205">
                  Initial shortlist turnaround
                </div>
              </div>
            </div>

            <div className="mt-10 bg-white/5 border border-white/10 rounded-xl p-6 md:p-8" data-id="element-2206">
              <div className="flex flex-col md:flex-row items-start gap-6" data-id="element-2207">
                <div className="w-14 h-14 bg-[#0099A1]/20 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-2208">
                  <ClipboardCheckIcon className="h-7 w-7 text-[#0099A1]" data-id="element-2209" />
                </div>
                <div data-id="element-2210">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-2211">
                    Our Shortlist Promise
                  </h3>
                  <p className="text-white/60 leading-relaxed" data-id="element-2212">
                    Within 48 hours of your brief, we deliver 2–3 resort options
                    with: room block availability, meeting space specs,
                    all-inclusive rates, AV/production capabilities, and our
                    honest recommendation based on your specific requirements.
                    No generic brochures — real options with real numbers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 7. HOW IT WORKS ===== */}
      <section className="py-20 bg-white" data-id="element-2213">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2214">
          <div className="max-w-4xl mx-auto" data-id="element-2215">
            <div className="text-center mb-14" data-id="element-2216">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2217">
                How It Works
              </h2>
              <p className="text-lg text-[#374047]/70" data-id="element-2218">
                From first call to onsite execution — here's the process.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2219">
              <div className="relative" data-id="element-2220">
                <div className="w-14 h-14 bg-[#0099A1] rounded-2xl flex items-center justify-center text-white font-bold text-xl mb-5 shadow-md" data-id="element-2221">
                  1
                </div>
                <h3 className="font-bold text-[#374047] text-lg mb-2" data-id="element-2222">
                  Share Your Requirements
                </h3>
                <p className="text-sm text-[#374047]/60 leading-relaxed" data-id="element-2223">
                  Headcount, dates, budget range, meeting needs, and experience
                  expectations. 15-minute call or form.
                </p>
                <div className="hidden lg:block absolute top-7 left-full w-full" data-id="element-2224">
                  <ChevronRightIcon className="h-5 w-5 text-[#0099A1]/30 mx-auto" data-id="element-2225" />
                </div>
              </div>

              <div className="relative" data-id="element-2226">
                <div className="w-14 h-14 bg-[#0099A1] rounded-2xl flex items-center justify-center text-white font-bold text-xl mb-5 shadow-md" data-id="element-2227">
                  2
                </div>
                <h3 className="font-bold text-[#374047] text-lg mb-2" data-id="element-2228">
                  Receive Your Shortlist
                </h3>
                <p className="text-sm text-[#374047]/60 leading-relaxed" data-id="element-2229">
                  Within 48 hours: 2–3 resort options with rates, meeting specs,
                  and budget bands. Our recommendation included.
                </p>
                <div className="hidden lg:block absolute top-7 left-full w-full" data-id="element-2230">
                  <ChevronRightIcon className="h-5 w-5 text-[#0099A1]/30 mx-auto" data-id="element-2231" />
                </div>
              </div>

              <div className="relative" data-id="element-2232">
                <div className="w-14 h-14 bg-[#0099A1] rounded-2xl flex items-center justify-center text-white font-bold text-xl mb-5 shadow-md" data-id="element-2233">
                  3
                </div>
                <h3 className="font-bold text-[#374047] text-lg mb-2" data-id="element-2234">
                  Contracting + Planning
                </h3>
                <p className="text-sm text-[#374047]/60 leading-relaxed" data-id="element-2235">
                  We handle resort contracts, room block management, AV
                  sourcing, F&B menus, transfers, and experience design.
                </p>
                <div className="hidden lg:block absolute top-7 left-full w-full" data-id="element-2236">
                  <ChevronRightIcon className="h-5 w-5 text-[#0099A1]/30 mx-auto" data-id="element-2237" />
                </div>
              </div>

              <div data-id="element-2238">
                <div className="w-14 h-14 bg-[#FF6B65] rounded-2xl flex items-center justify-center text-white font-bold text-xl mb-5 shadow-md" data-id="element-2239">
                  4
                </div>
                <h3 className="font-bold text-[#374047] text-lg mb-2" data-id="element-2240">
                  Onsite Execution
                </h3>
                <p className="text-sm text-[#374047]/60 leading-relaxed" data-id="element-2241">
                  Our team is on the ground in Cancun managing every detail — so
                  your team can focus on content and connection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 8. PROCUREMENT-FRIENDLY ===== */}
      <section className="py-12 bg-[#F4E8D0]/25" data-id="element-2242">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2243">
          <div className="max-w-3xl mx-auto" data-id="element-2244">
            <div className="bg-white rounded-xl border-2 border-[#0099A1]/20 p-6 md:p-8 flex flex-col md:flex-row items-start gap-5" data-id="element-2245">
              <div className="w-12 h-12 bg-[#0099A1]/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-2246">
                <FileTextIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2247" />
              </div>
              <div data-id="element-2248">
                <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-2249">
                  Procurement-Friendly
                </h3>
                <p className="text-[#374047]/70 leading-relaxed mb-3" data-id="element-2250">
                  We work with procurement teams and MSA requirements regularly.
                  Expect clear budgets, defined timelines, itemized
                  deliverables, and transparent pricing — no surprises.
                </p>
                <div className="flex flex-wrap gap-3 text-xs" data-id="element-2251">
                  <span className="inline-flex items-center px-3 py-1 bg-[#0099A1]/8 text-[#0099A1] rounded-full font-medium" data-id="element-2252">
                    <CheckIcon className="h-3 w-3 mr-1" data-id="element-2253" /> MSA-compatible
                  </span>
                  <span className="inline-flex items-center px-3 py-1 bg-[#0099A1]/8 text-[#0099A1] rounded-full font-medium" data-id="element-2254">
                    <CheckIcon className="h-3 w-3 mr-1" data-id="element-2255" /> Itemized budgets
                  </span>
                  <span className="inline-flex items-center px-3 py-1 bg-[#0099A1]/8 text-[#0099A1] rounded-full font-medium" data-id="element-2256">
                    <CheckIcon className="h-3 w-3 mr-1" data-id="element-2257" /> SOW + timelines
                  </span>
                  <span className="inline-flex items-center px-3 py-1 bg-[#0099A1]/8 text-[#0099A1] rounded-full font-medium" data-id="element-2258">
                    <CheckIcon className="h-3 w-3 mr-1" data-id="element-2259" /> W-9 / insurance on
                    file
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 9. LEAD FORM + CALENDLY ===== */}
      <section className="py-20 bg-white" data-id="element-2260">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2261">
          <div className="max-w-4xl mx-auto" data-id="element-2262">
            <div className="text-center mb-12" data-id="element-2263">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2264">
                Get Your Cancun SKO Proposal
              </h2>
              <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-2265">
                Tell us about your event and we'll send a tailored proposal with
                resort options and budget bands within 48 hours.
              </p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8" data-id="element-2266">
              {/* Form */}
              <div className="lg:col-span-3" data-id="element-2267">
                {formSubmitted ? <div className="bg-[#0099A1]/5 border-2 border-[#0099A1]/20 rounded-2xl p-10 text-center" data-id="element-2268">
                    <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2269">
                      <CheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2270" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#374047] mb-3" data-id="element-2271">
                      Request Received
                    </h3>
                    <p className="text-[#374047]/70 mb-6" data-id="element-2272">
                      We'll review your details and send a tailored proposal
                      with 2–3 resort options within 48 hours.
                    </p>
                    <Button variant="outline" onClick={() => setFormSubmitted(false)} data-id="element-2273">
                      Submit Another Request
                    </Button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2274">
                    <div className="grid sm:grid-cols-2 gap-5" data-id="element-2275">
                      <div data-id="element-2276">
                        <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2277">
                          Full Name *
                        </label>
                        <input type="text" name="name" value={formData.name} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none" placeholder="Jane Smith" data-id="element-2278" />
                      </div>
                      <div data-id="element-2279">
                        <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2280">
                          Title *
                        </label>
                        <input type="text" name="title" value={formData.title} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none" placeholder="VP Sales" data-id="element-2281" />
                      </div>
                    </div>

                    <div data-id="element-2282">
                      <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2283">
                        Company *
                      </label>
                      <input type="text" name="company" value={formData.company} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none" placeholder="Acme Corp" data-id="element-2284" />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5" data-id="element-2285">
                      <div data-id="element-2286">
                        <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2287">
                          Company Size
                        </label>
                        <select name="employeeBand" value={formData.employeeBand} onChange={handleFormChange} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none bg-white" data-id="element-2288">
                          <option value="" data-id="element-2289">Select...</option>
                          <option value="300-500" data-id="element-2290">300–500 employees</option>
                          <option value="500-1000" data-id="element-2291">500–1,000 employees</option>
                          <option value="1000-3000" data-id="element-2292">
                            1,000–3,000 employees
                          </option>
                          <option value="3000-5000" data-id="element-2293">
                            3,000–5,000 employees
                          </option>
                          <option value="5000+" data-id="element-2294">5,000+ employees</option>
                        </select>
                      </div>
                      <div data-id="element-2295">
                        <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2296">
                          Event Headcount
                        </label>
                        <select name="headcountBand" value={formData.headcountBand} onChange={handleFormChange} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none bg-white" data-id="element-2297">
                          <option value="" data-id="element-2298">Select...</option>
                          <option value="60-120" data-id="element-2299">60–120 people</option>
                          <option value="150-300" data-id="element-2300">150–300 people</option>
                          <option value="300-600+" data-id="element-2301">300–600+ people</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5" data-id="element-2302">
                      <div data-id="element-2303">
                        <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2304">
                          Target Month
                        </label>
                        <select name="targetMonth" value={formData.targetMonth} onChange={handleFormChange} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none bg-white" data-id="element-2305">
                          <option value="" data-id="element-2306">Select...</option>
                          <option value="Jan" data-id="element-2307">January</option>
                          <option value="Feb" data-id="element-2308">February</option>
                          <option value="Mar" data-id="element-2309">March</option>
                          <option value="Apr" data-id="element-2310">April</option>
                          <option value="May" data-id="element-2311">May</option>
                          <option value="Jun" data-id="element-2312">June</option>
                          <option value="Jul" data-id="element-2313">July</option>
                          <option value="Aug" data-id="element-2314">August</option>
                          <option value="Sep" data-id="element-2315">September</option>
                          <option value="Oct" data-id="element-2316">October</option>
                          <option value="Nov" data-id="element-2317">November</option>
                          <option value="Dec" data-id="element-2318">December</option>
                        </select>
                      </div>
                      <div data-id="element-2319">
                        <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2320">
                          Nights
                        </label>
                        <select name="formNights" value={formData.formNights} onChange={handleFormChange} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none bg-white" data-id="element-2321">
                          <option value="" data-id="element-2322">Select...</option>
                          <option value="2" data-id="element-2323">2 nights</option>
                          <option value="3" data-id="element-2324">3 nights</option>
                          <option value="4" data-id="element-2325">4 nights</option>
                        </select>
                      </div>
                    </div>

                    <div data-id="element-2326">
                      <label className="block text-sm font-semibold text-[#374047] mb-2" data-id="element-2327">
                        Currently Considering
                      </label>
                      <select name="considering" value={formData.considering} onChange={handleFormChange} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none bg-white" data-id="element-2328">
                        <option value="" data-id="element-2329">Select...</option>
                        <option value="domestic" data-id="element-2330">Domestic only</option>
                        <option value="cancun" data-id="element-2331">Cancun</option>
                        <option value="both" data-id="element-2332">Both domestic & Cancun</option>
                        <option value="unsure" data-id="element-2333">Not sure yet</option>
                      </select>
                    </div>

                    <Button variant="primary" size="lg" type="submit" className="w-full" data-id="element-2334">
                      <SendIcon className="h-4 w-4 mr-2" data-id="element-2335" />
                      Get My SKO Proposal
                    </Button>
                  </form>}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-2 space-y-6" data-id="element-2336">
                <div className="bg-[#374047] rounded-2xl p-6 text-white" data-id="element-2337">
                  <div className="flex items-center gap-3 mb-4" data-id="element-2338">
                    <PhoneIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2339" />
                    <h3 className="font-bold text-lg" data-id="element-2340">Prefer a Quick Call?</h3>
                  </div>
                  <p className="text-white/70 text-sm mb-5 leading-relaxed" data-id="element-2341">
                    Book a 15-minute call and we'll walk through your
                    requirements live. No pitch — just logistics.
                  </p>
                  <Button variant="primary" className="w-full" href="https://calendly.com/ashsingh/destameet-discovery-call" data-id="element-2342">
                    <CalendarIcon className="h-4 w-4 mr-2" data-id="element-2343" />
                    Book 15-Min Call
                  </Button>
                </div>

                <div className="bg-[#F4E8D0]/30 rounded-2xl p-6" data-id="element-2344">
                  <h3 className="font-bold text-[#374047] mb-4" data-id="element-2345">
                    What happens next:
                  </h3>
                  <div className="space-y-4" data-id="element-2346">
                    <div className="flex items-start gap-3" data-id="element-2347">
                      <div className="w-7 h-7 bg-[#0099A1] rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" data-id="element-2348">
                        1
                      </div>
                      <p className="text-sm text-[#374047]/70" data-id="element-2349">
                        We review your requirements (same day)
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-2350">
                      <div className="w-7 h-7 bg-[#0099A1] rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" data-id="element-2351">
                        2
                      </div>
                      <p className="text-sm text-[#374047]/70" data-id="element-2352">
                        You receive 2–3 resort options with rates (within 48hrs)
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-2353">
                      <div className="w-7 h-7 bg-[#0099A1] rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" data-id="element-2354">
                        3
                      </div>
                      <p className="text-sm text-[#374047]/70" data-id="element-2355">
                        We hop on a call to walk through the options
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center text-sm text-[#374047]/50" data-id="element-2356">
                  <p data-id="element-2357">Questions? Email us directly:</p>
                  <a href="mailto:experience@destameet.com" className="text-[#0099A1] font-medium hover:underline" data-id="element-2358">
                    experience@destameet.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 10. CTA SECTION ===== */}
      <CtaSection data-id="element-2359" />
    </div>;
}